import { ref } from 'vue';
import { config } from '@/config';

/**
 * useSgaCambioAsiento
 *
 * Handles seat change flow for SGA season ticket subscriptions.
 * Manages zone selection, seat validation, and cart creation.
 *
 * @param {Object} store - Vuex store instance
 * @param {Function} t - i18n translate function
 * @returns {Object} State and methods for seat change flow
 */
export default function useSgaCambioAsiento(store, t) {
    // Wizard steps: 'zones' | 'seats' | 'confirmation'
    const step = ref('zones');

    // Zones step
    const zones = ref([]);
    const venueSvg = ref(null);
    const selectedZone = ref(null);

    // Available zones for seat change (cross-referenced from precios endpoint)
    const availableZoneIds = ref(new Set());
    const zonePriceDiff = ref({});

    // Seats step
    const seats = ref([]);
    const selectedSeat = ref(null);
    const selectedSeatInfo = ref(null);

    // Confirmation step
    const priceDiff = ref(null);
    const cartData = ref(null);

    // Common state
    const loading = ref(false);
    const error = ref(null);
    const errorMessage = ref('');

    /**
     * Fetch available zone pricing for seat change.
     * Without seat_id, returns all available zones with price differences.
     */
    const fetchPrecios = async abonadoId => {
        loading.value = true;
        error.value = null;
        errorMessage.value = '';

        try {
            const response = await store.dispatch(
                config.getVuexAlias('sga.profile.abono.cambio-asiento.precios'),
                { abonado_id: abonadoId }
            );

            const data = response.data?.data || response.data || {};
            const zonas = data.zonas || [];

            availableZoneIds.value = new Set(zonas.map(z => z.zoneId));
            zonePriceDiff.value = zonas.reduce((acc, z) => {
                acc[z.zoneId] = z.priceDifference;
                return acc;
            }, {});

            return zonas;
        } catch (err) {
            handleError(err, 'perfil_sga.error_cargar_zonas');
            return [];
        } finally {
            loading.value = false;
        }
    };

    /**
     * Validate a seat selection and get price difference.
     */
    const validateSeat = async(abonadoId, seatId, seatInfo) => {
        error.value = null;
        errorMessage.value = '';

        try {
            const response = await store.dispatch(
                config.getVuexAlias('sga.profile.abono.cambio-asiento.precios'),
                { abonado_id: abonadoId, seat_id: seatId }
            );

            const data = response.data?.data || response.data;

            // Normalize: API returns priceDifference, components expect amount
            priceDiff.value = {
                amount: data.priceDifference ?? 0,
                seatId: data.seatId || seatId,
                priceManagement: response.data?.price_management ?? 0
            };
            selectedSeat.value = seatId;
            if (seatInfo) {
                selectedSeatInfo.value = seatInfo;
            }

            return data;
        } catch (err) {
            handleError(err, 'perfil_sga.error_validar_asiento');
            return null;
        }
    };

    /**
     * Check if a zone is available for seat change.
     */
    const isZoneAvailable = zone => {
        if (!zone) return false;
        const zoneId = zone.sga_id || zone.id;
        return availableZoneIds.value.has(zoneId);
    };

    /**
     * Get price difference for a zone.
     */
    const getZonePriceDiff = zone => {
        if (!zone) return null;
        const zoneId = zone.sga_id || zone.id;
        return zonePriceDiff.value[zoneId] || null;
    };

    /**
     * Fetch seats for a zone via sga.zonas.asientos.
     */
    const fetchSeats = async(eventId, zone) => {
        loading.value = true;
        error.value = null;
        errorMessage.value = '';

        try {
            const response = await store.dispatch(
                config.getVuexAlias('sga.zonas.asientos'),
                {
                    event_id: eventId,
                    zona_id: zone.id || zone.sga_id,
                    filters: {
                        group: 1
                    }
                }
            );

            const zoneDetail = response.data?.data || {};
            selectedZone.value = { ...zone, ...zoneDetail };

            // SeatMapSga expects 'configuracion' as filas
            // configuracion can be an object (rows keyed by alias, e.g. fila_1: { alias, asientos })
            // or an array (each element is a row). SeatMapSga handles both via Object.values/Array.isArray.
            // Pass through directly — do NOT wrap, or rows get nested in a single parent element.
            seats.value = zoneDetail.configuracion || zoneDetail.configuration || [];
        } catch (err) {
            handleError(err, 'perfil_sga.error_cargar_asientos');
        } finally {
            loading.value = false;
        }
    };

    /**
     * Select a zone and load its seats.
     */
    const selectZone = async(eventId, zone) => {
        if (!zone || !isZoneAvailable(zone)) {
            return;
        }

        selectedZone.value = zone;
        selectedSeat.value = null;
        selectedSeatInfo.value = null;
        priceDiff.value = null;
        seats.value = [];
        step.value = 'seats'; // Switch view immediately so the loading spinner shows

        if (eventId) {
            await fetchSeats(eventId, zone);
        }
    };

    /**
     * Create cart for seat change.
     */
    const createCart = async(abonadoId, seatId) => {
        loading.value = true;
        error.value = null;
        errorMessage.value = '';

        try {
            const response = await store.dispatch(
                config.getVuexAlias('sga.profile.abono.cambio-asiento.carrito'),
                { abonado_id: abonadoId, new_seat_id: seatId }
            );

            cartData.value = response.data?.data || response.data;

            return cartData.value;
        } catch (err) {
            handleError(err, 'perfil_sga.error_crear_carrito');
            return null;
        } finally {
            loading.value = false;
        }
    };

    /**
     * Go back to zones step.
     */
    const goBackToZones = () => {
        step.value = 'zones';
        selectedZone.value = null;
        selectedSeat.value = null;
        priceDiff.value = null;
        seats.value = [];
    };

    /**
     * Go back to seats step from confirmation.
     */
    const goBackToSeats = () => {
        step.value = 'seats';
        selectedSeat.value = null;
        priceDiff.value = null;
    };

    /**
     * Proceed to confirmation step after seat validation.
     */
    const confirmSeatSelection = () => {
        if (!selectedSeat.value || !priceDiff.value) {
            return;
        }
        step.value = 'confirmation';
    };

    /**
     * Reset all state.
     */
    const reset = () => {
        step.value = 'zones';
        zones.value = [];
        venueSvg.value = null;
        selectedZone.value = null;
        availableZoneIds.value = new Set();
        zonePriceDiff.value = {};
        seats.value = [];
        selectedSeat.value = null;
        selectedSeatInfo.value = null;
        priceDiff.value = null;
        cartData.value = null;
        loading.value = false;
        error.value = null;
        errorMessage.value = '';
    };

    /**
     * Error handler.
     */
    const handleError = (err, defaultKey) => {
        const status = err.response?.status;
        const serverMessage = err.response?.data?.message || err.response?.data?.error;

        if (serverMessage) {
            errorMessage.value = serverMessage;
        } else {
            errorMessage.value = t(defaultKey) || t('perfil_sga.error_desconocido');
        }

        error.value = err;

        if (process.env.NODE_ENV === 'development') {
            console.error('[useSgaCambioAsiento] Error:', err);
        }
    };

    return {
        // State
        step,
        zones,
        venueSvg,
        selectedZone,
        availableZoneIds,
        zonePriceDiff,
        seats,
        selectedSeat,
        selectedSeatInfo,
        priceDiff,
        cartData,
        loading,
        error,
        errorMessage,

        // Methods
        fetchPrecios,
        fetchSeats,
        validateSeat,
        isZoneAvailable,
        getZonePriceDiff,
        selectZone,
        createCart,
        goBackToZones,
        goBackToSeats,
        confirmSeatSelection,
        reset
    };
}
